/** Command-palette matching — pure, framework-free, unit-tested.
 *
 * The palette splits a query into keywords and matches by substring across ALL
 * of them (AND semantics): "bus stop" matches "Bus stops", "bus 123" pairs the
 * keyword "bus" with the ID 123 to offer "Bus stop #123".
 *
 * A query may carry, in any order:
 *  - an integer ID (`123`), a UUID (`0197a001-…`) or a support ticket id
 *    (`TO-7KQ2M9XA`) → "Open by ID" shortcuts, filtered by the remaining keywords;
 *  - a create trigger (`create` / `new` / `add` / `invite`) → "Create new …"
 *    quick-links, filtered by the remaining keywords.
 *
 * `to` builders and `perm` strings are plain data so this module stays free of
 * React/router imports; the component maps `perm` through `hasPerm`.
 */

export const INT_RE = /^\d+$/;
export const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** A support ticket id as people quote it: `TO-` and 8 characters, any case (the backend reads
 *  Crockford's O / I / L itself). The prefix is required: a bare 8-letter word is not an id. */
export const TICKET_RE = /^to-[0-9a-z]{8}$/i;

/** Words that flip a query into "create" intent. A token counts as a trigger
 *  when it's a length-≥3 prefix of one of these (so "cre", "new", "add",
 *  "inv"/"invite" all trigger, but a stray 1–2 char token never does). */
const CREATE_TRIGGERS = ['create', 'new', 'add', 'invite'];

export function isCreateTrigger(token: string): boolean {
  return token.length >= 3 && CREATE_TRIGGERS.some((w) => w.startsWith(token));
}

/** Lowercased, whitespace-split, non-empty tokens. */
export function tokenize(query: string): string[] {
  return query.trim().toLowerCase().split(/\s+/).filter(Boolean);
}

/** Per-token match tier: word-prefix beats substring; 0 = no match.
 *  (The pre-refactor single-token `score()`, applied one keyword at a time.) */
export function scoreToken(token: string, text: string): number {
  const q = token.toLowerCase();
  const t = text.toLowerCase();
  if (t.startsWith(q)) return 3;
  if (t.split(/[\s/·-]+/).some((w) => w.startsWith(q))) return 2;
  if (t.includes(q)) return 1;
  return 0;
}

/** AND across keywords: 0 unless EVERY token matches `text`, else the summed
 *  rank (so more/earlier-prefix matches sort higher). No tokens → base 1. */
export function matchAll(tokens: string[], text: string): number {
  if (tokens.length === 0) return 1;
  let total = 0;
  for (const tok of tokens) {
    const s = scoreToken(tok, text);
    if (s === 0) return 0;
    total += s;
  }
  return total;
}

export interface ParsedQuery {
  raw: string;
  /** All lowercased tokens — used to match pages/recents/entities (full AND). */
  tokens: string[];
  /** Tokens minus the recognised ID and minus create triggers — the keywords
   *  that filter the ID and Create groups. */
  filterTokens: string[];
  /** First integer token, if any. */
  intId?: string;
  /** First UUID token, if any. */
  uuidId?: string;
  /** First support ticket id, upper-cased (`TO-7KQ2M9XA`), if any. */
  ticketId?: string;
  /** A create trigger is present. */
  wantsCreate: boolean;
}

export function parseQuery(query: string): ParsedQuery {
  const tokens = tokenize(query);
  let intId: string | undefined;
  let uuidId: string | undefined;
  let ticketId: string | undefined;
  let wantsCreate = false;
  const filterTokens: string[] = [];
  for (const tok of tokens) {
    if (INT_RE.test(tok)) {
      if (intId === undefined) intId = tok;
    } else if (UUID_RE.test(tok)) {
      if (uuidId === undefined) uuidId = tok;
    } else if (TICKET_RE.test(tok)) {
      if (ticketId === undefined) ticketId = tok.toUpperCase();
    } else if (isCreateTrigger(tok)) {
      wantsCreate = true;
    } else {
      filterTokens.push(tok);
    }
  }
  return { raw: query, tokens, filterTokens, intId, uuidId, ticketId, wantsCreate };
}

export type IdKind = 'int' | 'uuid' | 'ticket';

export interface IdShortcut {
  label: string;
  kind: IdKind;
  to: (id: string) => string;
  /** View permission; undefined = always available (e.g. background tasks). */
  perm?: string;
  /** Synonyms not already in `label`, for the keyword filter. */
  keywords?: string[];
}

/** "Open by ID" targets. Integer-keyed records plus the one UUID-keyed record
 *  (delivery logs) and support tickets (`TO-…`). Filtered by the query's keywords, then by
 *  `hasPerm`. */
export const ID_SHORTCUTS: IdShortcut[] = [
  { label: 'User', kind: 'int', to: (id) => `/users/${id}`, perm: 'console_user_view' },
  { label: 'Tour operator', kind: 'int', to: (id) => `/tour-operators/${id}`, perm: 'console_top_view', keywords: ['top'] },
  { label: 'Tour', kind: 'int', to: (id) => `/tours/${id}`, perm: 'console_tours_view' },
  // The console pk, like every other target here — `/bookings?q=id=n` searched the
  // booking-system custom id instead and landed on the wrong booking (or none).
  { label: 'Booking', kind: 'int', to: (id) => `/bookings/${id}`, perm: 'console_bookings_view' },
  { label: 'Task', kind: 'int', to: (id) => `/bgtasks/${id}`, keywords: ['background', 'job', 'bgtask'] },
  { label: 'Region', kind: 'int', to: (id) => `/regions/${id}`, perm: 'console_regions_view' },
  { label: 'Trip type', kind: 'int', to: (id) => `/trip-types/${id}`, perm: 'console_trip_types_view' },
  { label: 'Pre-trip item', kind: 'int', to: (id) => `/pre-trip-items/${id}`, perm: 'console_pretrip_view', keywords: ['pretrip'] },
  {
    label: 'Pre-trip category',
    kind: 'int',
    to: (id) => `/pre-trip-item-categories/${id}`,
    perm: 'console_pretrip_view',
    keywords: ['pretrip'],
  },
  { label: 'Activity', kind: 'int', to: (id) => `/activities/${id}`, perm: 'console_activities_view' },
  { label: 'Activity category', kind: 'int', to: (id) => `/activity-categories/${id}`, perm: 'console_activities_view' },
  { label: 'Bus stop', kind: 'int', to: (id) => `/bus-stops/${id}`, perm: 'console_fleet_view' },
  { label: 'Line set', kind: 'int', to: (id) => `/line-sets/${id}`, perm: 'console_fleet_view', keywords: ['route'] },
  { label: 'Itinerary', kind: 'int', to: (id) => `/itineraries/${id}`, perm: 'console_itineraries_view' },
  { label: 'Tag', kind: 'int', to: (id) => `/tags/${id}`, perm: 'console_tags_view' },
  { label: 'Tag category', kind: 'int', to: (id) => `/tag-categories/${id}`, perm: 'console_tag_categories_view' },
  { label: 'Tag schema', kind: 'int', to: (id) => `/tag-schemas/${id}`, perm: 'console_tag_schemas_view', keywords: ['schema'] },
  { label: 'OTA', kind: 'int', to: (id) => `/otas/${id}`, perm: 'console_ota_view', keywords: ['online travel agency'] },
  { label: 'API credential', kind: 'int', to: (id) => `/api-credentials/${id}`, perm: 'console_api_credentials_view', keywords: ['key', 'token', 'credential'] },
  { label: 'Onboarding invite', kind: 'int', to: (id) => `/onboarding-invites/${id}`, perm: 'console_onboarding_invites_view', keywords: ['invite', 'signup', 'link', 'qr'] },
  { label: 'Order', kind: 'int', to: (id) => `/orders/${id}`, perm: 'console_orders_view' },
  { label: 'Notification', kind: 'int', to: (id) => `/notifications/${id}`, perm: 'console_notifications_view' },
  { label: 'Ruleset', kind: 'int', to: (id) => `/notifications/rulesets/${id}`, perm: 'console_notification_rulesets_view', keywords: ['rule', 'notification'] },
  { label: 'Session', kind: 'int', to: (id) => `/users/sessions/${id}`, perm: 'console_session_view', keywords: ['login'] },
  {
    label: 'Platform notice',
    kind: 'int',
    to: (id) => `/platform-notices?edit=${id}`,
    perm: 'console_platform_notices_manage',
    keywords: ['outage', 'incident', 'mcp'],
  },
  {
    label: 'Delivery log',
    kind: 'uuid',
    to: (id) => `/delivery-logs/${id}`,
    perm: 'console_event_delivery_view',
    keywords: ['edl', 'event'],
  },
  {
    label: 'Support ticket',
    kind: 'ticket',
    to: (id) => `/support-tickets/${id}`,
    perm: 'console_support_tickets_view',
    keywords: ['escalation', 'mcp'],
  },
];

export interface CreateTarget {
  label: string;
  /** Destination carrying the `?new` param the target page's `useCreateIntent`
   *  reacts to. Kept in sync with that page's hook value. */
  to: string;
  perm: string;
  keywords?: string[];
}

/** "Create new …" quick-links. Create forms are modal-only — so `to` opens the
 *  list route with `?new` (see `useCreateIntent`) — except the tour-operator
 *  wizard, which is a routed page (`/tour-operators/new`; legacy `?new=1` links
 *  still redirect there). Gated by the resource's `_manage` perm (user creation
 *  is an invite → `console_user_invite`). */
export const CREATE_TARGETS: CreateTarget[] = [
  { label: 'Create new tour operator', to: '/tour-operators/new', perm: 'console_top_manage', keywords: ['top', 'setup', 'wizard'] },
  { label: 'Create new tour', to: '/tours?new=1', perm: 'console_tours_manage' },
  { label: 'Create new itinerary', to: '/itineraries?new=1', perm: 'console_itineraries_manage' },
  { label: 'Create new trip type', to: '/trip-types?new=1', perm: 'console_trip_types_manage' },
  { label: 'Create new region', to: '/regions?new=1', perm: 'console_regions_manage' },
  { label: 'Create new trip item', to: '/pre-trip-items?new=item', perm: 'console_pretrip_manage', keywords: ['pretrip', 'checklist'] },
  { label: 'Create new trip item category', to: '/pre-trip-items?new=category', perm: 'console_pretrip_manage', keywords: ['pretrip'] },
  { label: 'Create new activity', to: '/activities?new=1', perm: 'console_activities_manage' },
  { label: 'Create new activity category', to: '/activity-categories?new=1', perm: 'console_activities_manage' },
  { label: 'Create new vehicle', to: '/vehicles?new=1', perm: 'console_fleet_manage' },
  { label: 'Create new bus stop', to: '/bus-stops?new=1', perm: 'console_fleet_manage' },
  { label: 'Create new line set', to: '/line-sets?new=1', perm: 'console_fleet_manage', keywords: ['route'] },
  { label: 'Create new OTA', to: '/otas?new=1', perm: 'console_ota_manage', keywords: ['online travel agency', 'distribution'] },
  { label: 'Create new launch policy', to: '/launch-policies?new=1', perm: 'console_launch_policy_manage' },
  { label: 'Create new tag', to: '/tags?new=1', perm: 'console_tags_manage' },
  { label: 'Create new tag category', to: '/tag-categories?new=1', perm: 'console_tag_categories_manage' },
  { label: 'Create new tag schema', to: '/tag-schemas?new=1', perm: 'console_tag_schemas_manage' },
  { label: 'Create new tag name alias', to: '/tag-name-aliases?new=1', perm: 'console_tag_aliases_manage', keywords: ['canonical', 'spelling', 'reseller'] },
  { label: 'Create new API credential', to: '/api-credentials?new=1', perm: 'console_api_credentials_manage', keywords: ['key', 'token'] },
  { label: 'Create new invite link', to: '/onboarding-invites?new=1', perm: 'console_onboarding_invites_manage', keywords: ['onboarding', 'signup', 'self-signup', 'qr'] },
  { label: 'Create new blacklist rule', to: '/blacklists/rules?new=1', perm: 'console_blacklist_manage', keywords: ['block'] },
  { label: 'Create new blacklist group', to: '/blacklists/event-groups?new=1', perm: 'console_event_group_manage', keywords: ['event'] },
  { label: 'Create new notification', to: '/notifications?new=1', perm: 'console_notifications_manage' },
  { label: 'Create new ruleset', to: '/notifications/rulesets?new=1', perm: 'console_notification_rulesets_manage', keywords: ['rule', 'notification'] },
  { label: 'Create new notification block', to: '/notifications/blocks?new=1', perm: 'console_notification_blocks_manage' },
  { label: 'Create new block list', to: '/notifications/block-lists?new=1', perm: 'console_block_lists_manage' },
  { label: 'Create new notification theme', to: '/notifications/themes?new=1', perm: 'console_notification_themes_manage' },
  { label: 'Import design', to: '/notifications/design-imports?new=1', perm: 'console_composition_manage', keywords: ['figma', 'html', 'material', 'notification'] },
  { label: 'Create new language policy', to: '/notifications/language-policies?new=1', perm: 'console_language_policies_manage' },
  { label: 'Create new language alias', to: '/notifications/language-aliases?new=1', perm: 'console_language_aliases_manage' },
  { label: 'Create new feature flag', to: '/notifications/features?new=1', perm: 'console_notification_features_manage', keywords: ['notification'] },
  { label: 'Create new preflight template', to: '/preflight-templates?new=1', perm: 'console_preflight_manage', keywords: ['checklist'] },
  { label: 'Create new checklist group', to: '/preflight-groups?new=1', perm: 'console_preflight_manage', keywords: ['preflight'] },
  { label: 'Create new tag category set', to: '/tag-category-sets?new=1', perm: 'console_preflight_manage', keywords: ['preflight'] },
  { label: 'Invite user', to: '/users/system?new=1', perm: 'console_user_invite', keywords: ['staff', 'system', 'guest'] },
  { label: 'Create new platform notice', to: '/platform-notices?new=1', perm: 'console_platform_notices_manage', keywords: ['outage', 'incident', 'mcp'] },
];
